import type { Sprint } from '@nexo/types';
import { cn } from '@nexo/ui';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '../../lib/format';
import { formatShortDate, type BurndownPoint } from './sprintUtils';

const PLOT_HEIGHT = 180;
const AXIS_BAND = 22;
const LEFT = 32;
const RIGHT = 12;
const TOP = 8;

/** Width of an element, kept in sync as it resizes (charts draw at their real pixel size). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? 0));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** Up to 4 clean ticks from 0 to at least `max`. */
function ticks(max: number): number[] {
  const raw = Math.max(1, max) / 3;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? raw;
  return Array.from({ length: Math.ceil(Math.max(1, max) / step) + 1 }, (_, i) => i * step);
}

/** Card with title, legend, the chart and a «Ver tabla» toggle (the accessible twin). */
function ChartCard({
  title,
  subtitle,
  legend,
  table,
  children,
}: {
  title: string;
  subtitle?: string;
  legend: { label: string; swatch: ReactNode }[];
  table: { head: string[]; rows: (string | number)[][] };
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [showTable, setShowTable] = useState(false);
  return (
    <figure className="rounded-2xl border border-border bg-surface p-4 shadow-panel">
      <figcaption className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="mr-auto">
          <span className="block text-sm font-semibold">{title}</span>
          {subtitle && <span className="block text-xs text-muted">{subtitle}</span>}
        </span>
        {legend.map((item) => (
          <span key={item.label} className="flex items-center gap-1.5 text-xs text-muted">
            {item.swatch}
            {item.label}
          </span>
        ))}
        <button
          type="button"
          aria-pressed={showTable}
          onClick={() => setShowTable((v) => !v)}
          className="rounded-md px-1.5 py-0.5 text-xs text-muted hover:bg-surface-2 hover:text-text"
        >
          {showTable ? t('Ver gráfico') : t('Ver tabla')}
        </button>
      </figcaption>
      {showTable ? (
        <div className="scrollbar-thin max-h-64 overflow-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface-2 text-left text-xs text-muted">
              <tr>
                {table.head.map((h) => (
                  <th key={h} className="px-3 py-1.5 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border tabular-nums">
              {table.rows.map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => (
                    <td key={j} className="px-3 py-1.5">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        children
      )}
    </figure>
  );
}

const LineSwatch = ({ className }: { className: string }) => (
  <span aria-hidden className={cn('h-0.5 w-4 rounded-full', className)} />
);

function Tooltip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const flip = x > width - 150;
  return (
    <div
      role="status"
      className="pointer-events-none absolute top-1 z-10 rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs shadow-float"
      style={flip ? { right: width - x + 10 } : { left: x + 10 }}
    >
      {children}
    </div>
  );
}

/** Points left each day against the ideal straight line to zero. */
export function BurndownChart({ points }: { points: BurndownPoint[] }) {
  const { t } = useTranslation();
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(1, ...points.map((p) => Math.max(p.ideal, p.remaining ?? 0)));
  const yTicks = ticks(max);
  const top = yTicks[yTicks.length - 1] ?? max;
  const plotWidth = Math.max(0, width - LEFT - RIGHT);
  const x = (i: number) => LEFT + (points.length > 1 ? (i / (points.length - 1)) * plotWidth : 0);
  const y = (v: number) => TOP + PLOT_HEIGHT - (v / top) * PLOT_HEIGHT;
  const real = points.flatMap((p, i) => (p.remaining === null ? [] : [{ i, v: p.remaining }]));
  const path = (list: { i: number; v: number }[]) =>
    list.map((p, k) => `${k ? 'L' : 'M'}${x(p.i)},${y(p.v)}`).join(' ');
  const lastReal = real[real.length - 1];
  const labelEvery = Math.max(
    1,
    Math.ceil(points.length / Math.max(1, Math.floor(plotWidth / 56))),
  );
  const active = hover === null ? undefined : points[hover];

  return (
    <ChartCard
      title={t('Burndown')}
      subtitle={t('Puntos de historia pendientes cada día')}
      legend={[
        { label: t('Pendientes'), swatch: <LineSwatch className="bg-accent" /> },
        { label: t('Ideal'), swatch: <LineSwatch className="bg-border-strong" /> },
      ]}
      table={{
        head: [t('Día'), t('Pendientes'), t('Ideal')],
        rows: points.map((p) => [
          formatShortDate(p.date),
          p.remaining === null ? '—' : formatNumber(p.remaining),
          formatNumber(p.ideal, { maximumFractionDigits: 1 }),
        ]),
      }}
    >
      <div ref={ref} className="relative" onMouseLeave={() => setHover(null)}>
        {width > 0 && (
          <svg
            width={width}
            height={TOP + PLOT_HEIGHT + AXIS_BAND}
            role="img"
            aria-label={t('Gráfico burndown del sprint')}
            onMouseMove={(event) => {
              const left = event.currentTarget.getBoundingClientRect().left;
              const ratio = (event.clientX - left - LEFT) / Math.max(1, plotWidth);
              const i = Math.round(ratio * (points.length - 1));
              setHover(Math.min(points.length - 1, Math.max(0, i)));
            }}
          >
            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={LEFT}
                  x2={width - RIGHT}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--color-border)"
                  strokeWidth={1}
                />
                <text
                  x={LEFT - 8}
                  y={y(tick)}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-subtle text-[11px] tabular-nums"
                >
                  {formatNumber(tick)}
                </text>
              </g>
            ))}
            {points.map((p, i) =>
              // Every few days, plus the last one (dropping a stepped label that would touch it).
              i === points.length - 1 ||
              (i % labelEvery === 0 && points.length - 1 - i >= labelEvery) ? (
                <text
                  key={p.date}
                  x={x(i)}
                  y={TOP + PLOT_HEIGHT + 16}
                  textAnchor={i === 0 ? 'start' : i === points.length - 1 ? 'end' : 'middle'}
                  className="fill-subtle text-[11px]"
                >
                  {formatShortDate(p.date)}
                </text>
              ) : null,
            )}
            <path
              d={path(points.map((p, i) => ({ i, v: p.ideal })))}
              fill="none"
              stroke="var(--color-border-strong)"
              strokeWidth={2}
              strokeLinecap="round"
            />
            {real.length > 0 && (
              <>
                <path
                  d={`${path(real)} L${x(real[real.length - 1]?.i ?? 0)},${y(0)} L${x(0)},${y(0)} Z`}
                  fill="var(--color-accent)"
                  opacity={0.1}
                />
                <path
                  d={path(real)}
                  fill="none"
                  stroke="var(--color-accent)"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </>
            )}
            {active && hover !== null && (
              <line
                x1={x(hover)}
                x2={x(hover)}
                y1={TOP}
                y2={TOP + PLOT_HEIGHT}
                stroke="var(--color-border-strong)"
                strokeWidth={1}
              />
            )}
            {lastReal && (
              <circle
                cx={x(lastReal.i)}
                cy={y(lastReal.v)}
                r={4.5}
                fill="var(--color-accent)"
                stroke="var(--color-surface)"
                strokeWidth={2}
              />
            )}
          </svg>
        )}
        {active && hover !== null && (
          <Tooltip x={x(hover)} width={width}>
            <p className="font-medium">{formatShortDate(active.date)}</p>
            <p className="text-muted">
              {t('Pendientes')}:{' '}
              <span className="text-text tabular-nums">
                {active.remaining === null ? '—' : formatNumber(active.remaining)}
              </span>
            </p>
            <p className="text-muted">
              {t('Ideal')}:{' '}
              <span className="text-text tabular-nums">
                {formatNumber(active.ideal, { maximumFractionDigits: 1 })}
              </span>
            </p>
          </Tooltip>
        )}
      </div>
    </ChartCard>
  );
}

/** Points committed vs completed in each finished sprint (oldest first). */
export function VelocityChart({ sprints }: { sprints: Sprint[] }) {
  const { t } = useTranslation();
  const [ref, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(
    1,
    ...sprints.flatMap((s) => [s.committed?.points ?? 0, s.result?.points ?? 0]),
  );
  const yTicks = ticks(max);
  const top = yTicks[yTicks.length - 1] ?? max;
  const plotWidth = Math.max(0, width - LEFT - RIGHT);
  // Few sprints stay together on the left instead of spreading over the whole width.
  const band = sprints.length > 0 ? Math.min(120, plotWidth / sprints.length) : 0;
  const bar = Math.min(24, Math.max(6, (band - 16) / 2));
  const y = (v: number) => TOP + PLOT_HEIGHT - (v / top) * PLOT_HEIGHT;
  const active = hover === null ? undefined : sprints[hover];

  /** Column with a 4px rounded top, square at the baseline. */
  const column = (cx: number, value: number, fill: string) => {
    const h = (value / top) * PLOT_HEIGHT;
    if (h <= 0) return null;
    const r = Math.min(4, h, bar / 2);
    const x0 = cx;
    const y0 = TOP + PLOT_HEIGHT - h;
    return (
      <path
        d={`M${x0},${TOP + PLOT_HEIGHT} V${y0 + r} Q${x0},${y0} ${x0 + r},${y0} H${x0 + bar - r} Q${x0 + bar},${y0} ${x0 + bar},${y0 + r} V${TOP + PLOT_HEIGHT} Z`}
        fill={fill}
      />
    );
  };

  return (
    <ChartCard
      title={t('Velocidad')}
      subtitle={t('Puntos comprometidos al iniciar y completados al cerrar cada sprint')}
      legend={[
        {
          label: t('Comprometidos'),
          swatch: <span aria-hidden className="size-2.5 rounded-[3px] bg-subtle" />,
        },
        {
          label: t('Completados'),
          swatch: <span aria-hidden className="size-2.5 rounded-[3px] bg-accent" />,
        },
      ]}
      table={{
        head: [
          t('Sprint'),
          t('Comprometidos'),
          t('Completados'),
          t('Tareas hechas'),
          t('Pasaron al siguiente'),
        ],
        rows: sprints.map((s) => [
          s.name,
          s.committed?.points ?? '—',
          s.result?.points ?? '—',
          s.result?.tasks ?? '—',
          s.result?.carriedOver ?? '—',
        ]),
      }}
    >
      <div ref={ref} className="relative" onMouseLeave={() => setHover(null)}>
        {width > 0 && (
          <svg
            width={width}
            height={TOP + PLOT_HEIGHT + AXIS_BAND}
            role="img"
            aria-label={t('Gráfico de velocidad por sprint')}
          >
            {yTicks.map((tick) => (
              <g key={tick}>
                <line
                  x1={LEFT}
                  x2={width - RIGHT}
                  y1={y(tick)}
                  y2={y(tick)}
                  stroke="var(--color-border)"
                  strokeWidth={1}
                />
                <text
                  x={LEFT - 8}
                  y={y(tick)}
                  dy="0.32em"
                  textAnchor="end"
                  className="fill-subtle text-[11px] tabular-nums"
                >
                  {formatNumber(tick)}
                </text>
              </g>
            ))}
            {sprints.map((s, i) => {
              const center = LEFT + band * i + band / 2;
              return (
                <g key={s.id}>
                  {hover === i && (
                    <rect
                      x={LEFT + band * i + 2}
                      y={TOP}
                      width={Math.max(0, band - 4)}
                      height={PLOT_HEIGHT}
                      rx={6}
                      fill="var(--color-surface-2)"
                    />
                  )}
                  {column(center - bar - 1, s.committed?.points ?? 0, 'var(--color-subtle)')}
                  {column(center + 1, s.result?.points ?? 0, 'var(--color-accent)')}
                  <text
                    x={center}
                    y={TOP + PLOT_HEIGHT + 16}
                    textAnchor="middle"
                    className="fill-subtle text-[11px]"
                  >
                    {s.name.length > 12 ? `${s.name.slice(0, 11)}…` : s.name}
                  </text>
                  {/* Hit area: the whole band, much bigger than the bars. */}
                  <rect
                    x={LEFT + band * i}
                    y={TOP}
                    width={band}
                    height={PLOT_HEIGHT + AXIS_BAND}
                    fill="transparent"
                    onMouseEnter={() => setHover(i)}
                  />
                </g>
              );
            })}
          </svg>
        )}
        {active && hover !== null && (
          <Tooltip x={LEFT + band * hover + band / 2} width={width}>
            <p className="font-medium">{active.name}</p>
            <p className="text-muted">
              {t('Comprometidos')}:{' '}
              <span className="text-text tabular-nums">{active.committed?.points ?? '—'}</span>
            </p>
            <p className="text-muted">
              {t('Completados')}:{' '}
              <span className="text-text tabular-nums">{active.result?.points ?? '—'}</span>
            </p>
          </Tooltip>
        )}
      </div>
    </ChartCard>
  );
}
